/**
 * Pure yard-search helpers — prefix parsing + client-side filtering over the
 * cached yard snapshot (cars / tracks / work orders). No React, no fetch:
 * unit-tested under vitest (see __tests__/yardSearch.test.ts).
 *
 * NOTE (per review): useDeferredValue drives re-filter in the UI because all
 * matching is in-memory. If search ever becomes server-side, switch to a real
 * debounce (utils/debounce) before issuing requests.
 */

export type SearchScope = 'all' | 'cars' | 'tracks' | 'jobs'

export interface ParsedQuery {
  scope: SearchScope
  term: string
  /** True when scope came from an explicit `car:`/`trk:`/`job:` prefix
   *  (not chip state or marks auto-scope). */
  explicit: boolean
}

export interface CarHit {
  kind: 'car'
  id: string
  reportingMark: string
  /** Present only when the asset carries kinematics (on a track). */
  trackId?: string
  linearOffset?: number
  trackName?: string
  state?: string
}

export interface TrackHit {
  kind: 'track'
  id: string
  name: string
  trackType?: string
  carCount?: number
}

export interface JobHit {
  kind: 'job'
  id: string
  woNumber?: string
  swlNumber?: string
  status?: string
}

export type SearchHit = CarHit | TrackHit | JobHit

export interface YardSearchData {
  /** From GET /api/v1/yards/{fid}/assets — kinematics flattened. */
  cars: {
    id: string
    reportingMark: string
    trackId?: string
    linearOffset?: number
    state?: string
  }[]
  /** From GET /api/v1/tracks/routable. */
  tracks: {
    id: string
    name: string
    trackType?: string
  }[]
  /** From GET /api/v1/workorders. */
  jobs: {
    id: string
    woNumber?: string
    swlNumber?: string
    status?: string
  }[]
}

export interface GroupedResults {
  cars: CarHit[]
  tracks: TrackHit[]
  jobs: JobHit[]
  /** True when nothing matched in any in-scope group. */
  empty: boolean
}

/** Minimum characters before filtering engages (DOM-efficiency floor). */
export const MIN_QUERY_LENGTH = 2
/** Cap per result group — the panel is a jump list, not a report. */
export const MAX_PER_GROUP = 5

const SCOPE_PREFIXES: Record<string, SearchScope> = {
  car: 'cars',
  cars: 'cars',
  trk: 'tracks',
  track: 'tracks',
  job: 'jobs',
  jobs: 'jobs',
  swl: 'jobs',
  wo: 'jobs',
}

/** Reporting-mark shape: 1–4 letters (optional space) + digits. "CSXT 12345". */
const MARKS_PATTERN = /^[A-Za-z]{1,4}\s?\d{3,}$/

/**
 * Parse `car:foo` / `trk:x` / `job:y` prefixes and auto-scope reporting-mark
 * shaped input ("CSXT 12345" → cars). Explicit prefix always wins.
 */
export function parseSearchQuery(raw: string): ParsedQuery {
  const trimmed = raw.trim()
  const prefixed = trimmed.match(/^([a-zA-Z]{2,5})\s*:\s*(.*)$/)
  if (prefixed) {
    const scope = SCOPE_PREFIXES[prefixed[1].toLowerCase()]
    if (scope) {
      return { scope, term: prefixed[2].trim(), explicit: true }
    }
  }
  if (MARKS_PATTERN.test(trimmed)) {
    return { scope: 'cars', term: trimmed, explicit: false }
  }
  return { scope: 'all', term: trimmed, explicit: false }
}

const norm = (s: string | undefined | null) => (s ?? '').toLowerCase()

function carMatches(car: YardSearchData['cars'][number], q: string): boolean {
  // Reporting marks are matched space-insensitively: "CSXT 123" ≡ "CSXT123".
  const mark = norm(car.reportingMark).replace(/\s+/g, '')
  const needle = q.replace(/\s+/g, '')
  return mark.includes(needle) || norm(car.reportingMark).includes(q)
}

/**
 * Filter the yard snapshot by parsed query, honoring scope and per-group cap.
 * Pure — same input ⇒ same output.
 */
export function filterYard(data: YardSearchData, parsed: ParsedQuery): GroupedResults {
  const q = norm(parsed.term)
  if (q.length < MIN_QUERY_LENGTH) {
    return { cars: [], tracks: [], jobs: [], empty: false }
  }

  const trackNameById = new Map(data.tracks.map((t) => [t.id, t.name]))

  const cars: CarHit[] =
    parsed.scope === 'all' || parsed.scope === 'cars'
      ? data.cars
          .filter((c) => carMatches(c, q))
          .slice(0, MAX_PER_GROUP)
          .map((c) => ({
            kind: 'car' as const,
            id: c.id,
            reportingMark: c.reportingMark,
            trackId: c.trackId,
            linearOffset: c.linearOffset,
            trackName: c.trackId ? trackNameById.get(c.trackId) : undefined,
            state: c.state,
          }))
      : []

  const tracks: TrackHit[] =
    parsed.scope === 'all' || parsed.scope === 'tracks'
      ? data.tracks
          .filter((t) => norm(t.name).includes(q))
          .slice(0, MAX_PER_GROUP)
          .map((t) => ({ kind: 'track' as const, id: t.id, name: t.name, trackType: t.trackType }))
      : []

  const jobs: JobHit[] =
    parsed.scope === 'all' || parsed.scope === 'jobs'
      ? data.jobs
          .filter(
            (j) =>
              norm(j.woNumber).includes(q) ||
              norm(j.swlNumber).includes(q) ||
              norm(j.id).includes(q)
          )
          .slice(0, MAX_PER_GROUP)
          .map((j) => ({
            kind: 'job' as const,
            id: j.id,
            woNumber: j.woNumber,
            swlNumber: j.swlNumber,
            status: j.status,
          }))
      : []

  return { cars, tracks, jobs, empty: cars.length + tracks.length + jobs.length === 0 }
}

/**
 * Flatten grouped results into the single ordered list the combobox
 * renders and arrow-keys over (cars → tracks → jobs, matching the
 * panel's visual order).
 */
export function flattenSearchResults(results: GroupedResults): SearchHit[] {
  return [...results.cars, ...results.tracks, ...results.jobs]
}
